import { useCallback, useEffect, useState } from 'react'
import { Layout } from 'react-grid-layout'
import {
  flattenWidgets,
  mapLayoutDtoToLayout,
  mapWidgetDtoToWidget,
  serializeWidgetOptions,
} from '@/routes/Dashboard/utils'
import { useApiClient } from '@/utils/api/ApiClient'
import { ClientAreaApiClient } from '@/utils/api/clientAreaApi'
import { CreateUserDashboardLayoutRequest } from 'api/ClientArea/Dashboards'

import { ALLOWED_WIDGET_TYPES, DashboardLayout, Widget } from './types'

const IB_DASHBOARD_TYPE_ID = 3

interface UseDashboardResult {
  layouts: DashboardLayout[]
  availableWidgets: Widget[]
  isLoading: boolean
  error: Error | null
  createLayout: (
    name: string,
    description: string,
    widgets: Widget[],
    rglLayouts: Layout[]
  ) => Promise<string>
  updateLayout: (
    layoutId: string,
    name: string,
    description: string,
    widgets: Widget[],
    rglLayouts: Layout[]
  ) => Promise<void>
  deleteLayout: (layoutId: string) => Promise<void>
  refreshLayouts: () => Promise<void>
}

export function useDashboard(userId: string): UseDashboardResult {
  const apiClient = useApiClient(ClientAreaApiClient)
  const [layouts, setLayouts] = useState<DashboardLayout[]>([])
  const [availableWidgets, setAvailableWidgets] = useState<Widget[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchLayouts = useCallback(async (dashboardTypeIds: number[] = [IB_DASHBOARD_TYPE_ID]) => {
    if (!apiClient) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const [userLayouts, dashboardTypesResponse] = await Promise.all([
        apiClient.getUserDashboardsLayouts(userId, dashboardTypeIds),
        apiClient.getUserAvailableWidgets(userId, dashboardTypeIds),
      ])

      const allLayouts = userLayouts.map((dto) => {
        const layout = mapLayoutDtoToLayout(dto)

        return {
          ...layout,
          widgets: layout.widgets.filter((w) => ALLOWED_WIDGET_TYPES.includes(w.type)),
        }
      })

      setLayouts(allLayouts)

      const availableLayoutsData = dashboardTypesResponse[0]?.layouts || []
      const allAvailableWidgets: Widget[] = []

      availableLayoutsData.forEach((layout) => {
        const flattenedWidgets = flattenWidgets(layout.widgets || [])
        flattenedWidgets.forEach((widgetDto) => {
          const mapped = mapWidgetDtoToWidget(widgetDto)

          if (
            ALLOWED_WIDGET_TYPES.includes(mapped.type) &&
            !allAvailableWidgets.find((w) => w.id === mapped.id)
          ) {
            allAvailableWidgets.push(mapped)
          }
        })
      })

      setAvailableWidgets(allAvailableWidgets)
    } catch (err) {
      console.error('Failed to fetch dashboard layouts:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch layouts'))
      setLayouts([])
      setAvailableWidgets([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createLayout = useCallback(
    async (
      name: string,
      description: string,
      widgets: Widget[],
      rglLayouts: Layout[]
    ): Promise<string> => {
      if (!apiClient) {
        throw new Error('API client not available')
      }

      setError(null)

      const safeLayouts = normalizeForBackend(rglLayouts)

      const payload: CreateUserDashboardLayoutRequest = {
        userId,
        name,
        description,
        dashboardtypeId: IB_DASHBOARD_TYPE_ID,
        icon: '',
        widgetOptions: serializeWidgetOptions(widgets, safeLayouts),
      }

      const beforeIds = layouts.map((l) => l.id)

      await apiClient.createUserDashboardLayout(userId, payload)

      const updatedDtos = await apiClient.getUserDashboardsLayouts(userId, [IB_DASHBOARD_TYPE_ID])

      const mapped = updatedDtos.map(mapLayoutDtoToLayout)

      setLayouts(mapped)

      const created = mapped.find((l) => !beforeIds.includes(l.id))

      if (!created) {
        throw new Error('Created layout not found after refresh')
      }

      return created.id
    },
    [userId, apiClient, layouts]
  )
  const updateLayout = useCallback(
    async (
      layoutId: string,
      name: string,
      description: string,
      widgets: Widget[],
      rglLayouts: Layout[]
    ) => {
      if (!apiClient) throw new Error('API client not available')

      if (!layoutId) {
        const error = new Error('Layout ID is required for update')
        setError(error)
        throw error
      }

      try {
        setError(null)
        const safeLayouts = normalizeForBackend(rglLayouts)

        const payload: CreateUserDashboardLayoutRequest = {
          userId: userId,
          name,
          description,
          dashboardtypeId: IB_DASHBOARD_TYPE_ID,
          icon: '',
          widgetOptions: serializeWidgetOptions(widgets, safeLayouts),
        }

        await apiClient.updateUserDashboardLayout(userId, layoutId, payload)

        await fetchLayouts() // works now because fetchLayouts has a default param
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update layout')
        setError(error)
        throw error
      }
    },
    [fetchLayouts, userId]
  )
  const deleteLayout = useCallback(
    async (layoutId: string) => {
      if (!apiClient) {
        throw new Error('API client not available')
      }

      if (!layoutId) {
        const error = new Error('Layout ID is required for deletion')
        console.error('Failed to delete dashboard layout:', error)
        setError(error)
        throw error
      }

      try {
        setError(null)

        await apiClient.deleteUserDashboardLayout(userId, layoutId)

        setLayouts((prev) => prev.filter((layout) => layout.id !== layoutId))
      } catch (err) {
        console.error('Failed to delete dashboard layout:', err)
        const error = err instanceof Error ? err : new Error('Failed to delete layout')
        setError(error)
        throw error
      }
    },
    [userId]
  )

  useEffect(() => {
    fetchLayouts()
  }, [fetchLayouts])

  return {
    layouts,
    availableWidgets,
    isLoading,
    error,
    createLayout,
    updateLayout,
    deleteLayout,
    refreshLayouts: fetchLayouts,
  }
}
function hasOverlap(layouts: Layout[]): boolean {
  for (let i = 0; i < layouts.length; i++) {
    for (let j = i + 1; j < layouts.length; j++) {
      const a = layouts[i]
      const b = layouts[j]

      const overlap = a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y

      if (overlap) return true
    }
  }
  return false
}
function normalizeForBackend(layouts: Layout[]): Layout[] {
  if (!hasOverlap(layouts)) {
    return layouts
  }

  const groupedByColumn: Record<number, Layout[]> = {}

  layouts.forEach((l) => {
    if (!groupedByColumn[l.x]) groupedByColumn[l.x] = []
    groupedByColumn[l.x].push({ ...l })
  })

  Object.values(groupedByColumn).forEach((column) => {
    column
      .sort((a, b) => a.y - b.y)
      .reduce((accY, layout) => {
        layout.y = accY
        return accY + layout.h
      }, 0)
  })

  return Object.values(groupedByColumn).flat()
}
